export type UserRole = 'administrator' | 'user' | 'view_only';
export type UserStatus = 'active' | 'inactive' | 'suspended';
export type PermissionViewLevel = 'all' | 'assigned_default' | 'assigned_only' | 'none';
export type PermissionEditLevel = 'all' | 'assigned' | 'none';

export interface UserPermissions {
  manageUsers: boolean;
  manageCompanies: boolean;
  manageIntegrations: boolean;
  configureAutomation: boolean;
  configureAI: boolean;
  viewReports: boolean;
  manageAccounts: boolean;
  sendCommunications: boolean;
  logActivities: boolean;
  // Payment permissions
  viewPayments: boolean;
  allocatePayments: boolean;
  reverseAllocations: boolean;
  // Customer Accounts permissions
  customerAccountsView: PermissionViewLevel;
  customerAccountsEdit: PermissionEditLevel;
  customerAccountsReassign: boolean;
  // Collection Cases permissions
  collectionCasesView: PermissionViewLevel;
  collectionCasesCreate: boolean;
  collectionCasesEdit: PermissionEditLevel;
  collectionCasesDelete: boolean;
  collectionCasesReassign: boolean;
  // Promise to Pay permissions
  promiseToPayView: PermissionViewLevel;
  promiseToPayCreate: boolean;
  promiseToPayEdit: PermissionEditLevel;
  promiseToPayDelete: boolean;
  promiseToPayReassign: boolean;
  // Analytics & Reporting permissions
  viewCreditControlDashboard: boolean;
  viewCollectionsDashboard: boolean;
  viewDisputesDashboard: boolean;
  viewAgeingAnalysis: boolean;
  viewMultiCompanyDashboard: boolean;
  accessManagerViews: boolean;
  // Administration permissions - Manage Users
  adminManageUsersView: boolean;
  adminManageUsersCreate: boolean;
  adminManageUsersEdit: boolean;
  adminManageUsersDelete: boolean;
  // Administration permissions - Manage Companies
  adminManageCompaniesView: boolean;
  adminManageCompaniesCreate: boolean;
  adminManageCompaniesEdit: boolean;
  adminManageCompaniesDelete: boolean;
  // Administration permissions - Manage General Settings
  adminManageGeneralSettingsView: boolean;
  adminManageGeneralSettingsCreate: boolean;
  adminManageGeneralSettingsEdit: boolean;
  adminManageGeneralSettingsDelete: boolean;
  // Administration permissions - Manage Integrations
  adminManageIntegrationsView: boolean;
  adminManageIntegrationsCreate: boolean;
  adminManageIntegrationsEdit: boolean;
  adminManageIntegrationsDelete: boolean;
  // Administration permissions - Manage Templates
  adminManageTemplatesView: boolean;
  adminManageTemplatesCreate: boolean;
  adminManageTemplatesEdit: boolean;
  adminManageTemplatesDelete: boolean;
  // Administration permissions - Manage Collection Strategies
  adminManageCollectionStrategiesView: boolean;
  adminManageCollectionStrategiesCreate: boolean;
  adminManageCollectionStrategiesEdit: boolean;
  adminManageCollectionStrategiesDelete: boolean;
  // Administration permissions - Manage Dispute Types
  adminManageDisputeTypesView: boolean;
  adminManageDisputeTypesCreate: boolean;
  adminManageDisputeTypesEdit: boolean;
  adminManageDisputeTypesDelete: boolean;
  // Administration permissions - Configure AI
  adminConfigureAIView: boolean;
  adminConfigureAICreate: boolean;
  adminConfigureAIEdit: boolean;
  adminConfigureAIDelete: boolean;
}

export interface User {
  id: string;
  evoUserId?: string;
  externalFirstName?: string;
  externalLastName?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: UserRole;
  status: UserStatus;
  avatar?: string;
  companies: string[]; // company IDs
  companyCount: number;
  permissions: UserPermissions;
  lastLogin?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface UserFilters {
  search: string;
  role: UserRole | 'all';
  status: UserStatus | 'all';
  sortBy: 'name_asc' | 'name_desc' | 'recent' | 'role';
}

export const DEFAULT_PERMISSIONS: Record<UserRole, UserPermissions> = {
  administrator: {
    manageUsers: true,
    manageCompanies: true,
    manageIntegrations: true,
    configureAutomation: true,
    configureAI: true,
    viewReports: true,
    manageAccounts: true,
    sendCommunications: true,
    logActivities: true,
    viewPayments: true,
    allocatePayments: true,
    reverseAllocations: true,
    customerAccountsView: 'all',
    customerAccountsEdit: 'all',
    customerAccountsReassign: true,
    collectionCasesView: 'all',
    collectionCasesCreate: true,
    collectionCasesEdit: 'all',
    collectionCasesDelete: true,
    collectionCasesReassign: true,
    promiseToPayView: 'all',
    promiseToPayCreate: true,
    promiseToPayEdit: 'all',
    promiseToPayDelete: true,
    promiseToPayReassign: true,
    viewCreditControlDashboard: true,
    viewCollectionsDashboard: true,
    viewDisputesDashboard: true,
    viewAgeingAnalysis: true,
    viewMultiCompanyDashboard: true,
    accessManagerViews: true,
    adminManageUsersView: true,
    adminManageUsersCreate: true,
    adminManageUsersEdit: true,
    adminManageUsersDelete: true,
    adminManageCompaniesView: true,
    adminManageCompaniesCreate: true,
    adminManageCompaniesEdit: true,
    adminManageCompaniesDelete: true,
    adminManageGeneralSettingsView: true,
    adminManageGeneralSettingsCreate: true,
    adminManageGeneralSettingsEdit: true,
    adminManageGeneralSettingsDelete: true,
    adminManageIntegrationsView: true,
    adminManageIntegrationsCreate: true,
    adminManageIntegrationsEdit: true,
    adminManageIntegrationsDelete: true,
    adminManageTemplatesView: true,
    adminManageTemplatesCreate: true,
    adminManageTemplatesEdit: true,
    adminManageTemplatesDelete: true,
    adminManageCollectionStrategiesView: true,
    adminManageCollectionStrategiesCreate: true,
    adminManageCollectionStrategiesEdit: true,
    adminManageCollectionStrategiesDelete: true,
    adminManageDisputeTypesView: true,
    adminManageDisputeTypesCreate: true,
    adminManageDisputeTypesEdit: true,
    adminManageDisputeTypesDelete: true,
    adminConfigureAIView: true,
    adminConfigureAICreate: true,
    adminConfigureAIEdit: true,
    adminConfigureAIDelete: true,
  },
  user: {
    manageUsers: false,
    manageCompanies: false,
    manageIntegrations: false,
    configureAutomation: false,
    configureAI: false,
    viewReports: true,
    manageAccounts: true,
    sendCommunications: true,
    logActivities: true,
    viewPayments: true,
    allocatePayments: true,
    reverseAllocations: true,
    customerAccountsView: 'all',
    customerAccountsEdit: 'all',
    customerAccountsReassign: true,
    collectionCasesView: 'all',
    collectionCasesCreate: true,
    collectionCasesEdit: 'all',
    collectionCasesDelete: true,
    collectionCasesReassign: true,
    promiseToPayView: 'all',
    promiseToPayCreate: true,
    promiseToPayEdit: 'all',
    promiseToPayDelete: true,
    promiseToPayReassign: true,
    viewCreditControlDashboard: true,
    viewCollectionsDashboard: true,
    viewDisputesDashboard: true,
    viewAgeingAnalysis: true,
    viewMultiCompanyDashboard: true,
    accessManagerViews: true,
    adminManageUsersView: false,
    adminManageUsersCreate: false,
    adminManageUsersEdit: false,
    adminManageUsersDelete: false,
    adminManageCompaniesView: false,
    adminManageCompaniesCreate: false,
    adminManageCompaniesEdit: false,
    adminManageCompaniesDelete: false,
    adminManageGeneralSettingsView: false,
    adminManageGeneralSettingsCreate: false,
    adminManageGeneralSettingsEdit: false,
    adminManageGeneralSettingsDelete: false,
    adminManageIntegrationsView: false,
    adminManageIntegrationsCreate: false,
    adminManageIntegrationsEdit: false,
    adminManageIntegrationsDelete: false,
    adminManageTemplatesView: false,
    adminManageTemplatesCreate: false,
    adminManageTemplatesEdit: false,
    adminManageTemplatesDelete: false,
    adminManageCollectionStrategiesView: false,
    adminManageCollectionStrategiesCreate: false,
    adminManageCollectionStrategiesEdit: false,
    adminManageCollectionStrategiesDelete: false,
    adminManageDisputeTypesView: false,
    adminManageDisputeTypesCreate: false,
    adminManageDisputeTypesEdit: false,
    adminManageDisputeTypesDelete: false,
    adminConfigureAIView: false,
    adminConfigureAICreate: false,
    adminConfigureAIEdit: false,
    adminConfigureAIDelete: false,
  },
  view_only: {
    manageUsers: false,
    manageCompanies: false,
    manageIntegrations: false,
    configureAutomation: false,
    configureAI: false,
    viewReports: true,
    manageAccounts: false,
    sendCommunications: false,
    logActivities: false,
    viewPayments: true,
    allocatePayments: false,
    reverseAllocations: false,
    customerAccountsView: 'none',
    customerAccountsEdit: 'none',
    customerAccountsReassign: false,
    collectionCasesView: 'none',
    collectionCasesCreate: false,
    collectionCasesEdit: 'none',
    collectionCasesDelete: false,
    collectionCasesReassign: false,
    promiseToPayView: 'none',
    promiseToPayCreate: false,
    promiseToPayEdit: 'none',
    promiseToPayDelete: false,
    promiseToPayReassign: false,
    viewCreditControlDashboard: false,
    viewCollectionsDashboard: false,
    viewDisputesDashboard: false,
    viewAgeingAnalysis: false,
    viewMultiCompanyDashboard: false,
    accessManagerViews: false,
    adminManageUsersView: false,
    adminManageUsersCreate: false,
    adminManageUsersEdit: false,
    adminManageUsersDelete: false,
    adminManageCompaniesView: false,
    adminManageCompaniesCreate: false,
    adminManageCompaniesEdit: false,
    adminManageCompaniesDelete: false,
    adminManageGeneralSettingsView: false,
    adminManageGeneralSettingsCreate: false,
    adminManageGeneralSettingsEdit: false,
    adminManageGeneralSettingsDelete: false,
    adminManageIntegrationsView: false,
    adminManageIntegrationsCreate: false,
    adminManageIntegrationsEdit: false,
    adminManageIntegrationsDelete: false,
    adminManageTemplatesView: false,
    adminManageTemplatesCreate: false,
    adminManageTemplatesEdit: false,
    adminManageTemplatesDelete: false,
    adminManageCollectionStrategiesView: false,
    adminManageCollectionStrategiesCreate: false,
    adminManageCollectionStrategiesEdit: false,
    adminManageCollectionStrategiesDelete: false,
    adminManageDisputeTypesView: false,
    adminManageDisputeTypesCreate: false,
    adminManageDisputeTypesEdit: false,
    adminManageDisputeTypesDelete: false,
    adminConfigureAIView: false,
    adminConfigureAICreate: false,
    adminConfigureAIEdit: false,
    adminConfigureAIDelete: false,
  },
};

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  administrator: 'Full system access including user management, company settings, and all configurations',
  user: 'Can manage assigned accounts, send communications, and view reports',
  view_only: 'Read-only access to assigned accounts, communications, and reports',
};

export const ROLE_LABELS: Record<UserRole, string> = {
  administrator: 'Administrator',
  user: 'User',
  view_only: 'View Only',
};
